import React from 'react';
import { TMaterialTableColumns } from 'components/table/index.types';
import { transactions as types } from '../../../../types';
import timeFormat from 'utils/time-format';
import { Plank } from 'components/small';
import {
    BankList,
    EOperationType,
    ETransactionStatus,
    ETransactionType,
} from '../../../../types/entities/transactions';

type THandler = (data: types.TTransactions) => void;

const columns = ({
    handlers: { confirm, reject },
}: {
    handlers: {
        confirm: THandler;
        reject: THandler;
    };
}): TMaterialTableColumns<types.TTransactions> => [
    {
        width: '50',
        key: 'id',
        title: 'Transaction ID',
    },
    {
        key: 'updatedAt',
        title: 'Updated at',
        width: '170',
        render: (props) => {
            return <span>{timeFormat(props.rowData.updatedAt, false)}</span>;
        },
    },
    {
        width: '100',
        key: 'totalAmount',
        title: 'Total Amount',
    },
    {
        width: '100',
        key: 'amount',
        title: 'Transaction Amount',
    },
    {
        width: '100',
        key: 'feeAmount',
        title: 'Fee Amount',
    },
    {
        width: '100',
        key: 'nexId',
        title: 'NEX ID',
    },
    {
        width: '200',
        key: 'merchantId',
        title: 'Brand Partner ID',
    },
    {
        width: '100',
        key: 'type',
        title: 'Type',
        render: (props) => {
            const transType = (() => {
                switch (props.rowData.type) {
                    case ETransactionType.DEBIT: {
                        return 'Debit';
                    }
                    case ETransactionType.DIRECT_DEBIT: {
                        return 'Direct Debit';
                    }
                    case ETransactionType.TOP_UP: {
                        return 'Top up';
                    }
                    case ETransactionType.WALLET: {
                        return 'Wallet';
                    }
                    case ETransactionType.WITHDRAW: {
                        return 'Withdraw';
                    }
                    case ETransactionType.PULSA: {
                        return 'Pulse';
                    }
                    case ETransactionType.INTERBANK: {
                        return 'Interbank';
                    }
                    case ETransactionType.PAYMENT: {
                        return 'Payment';
                    }
                    case ETransactionType.QR_PAYMENT: {
                        return 'QRIS Payment';
                    }
                    case ETransactionType.QR_PAYMENT_REFUND: {
                        return 'QRIS Refund';
                    }
                    default:
                        return '';
                }
            })();
            return <span>{transType}</span>;
        },
    },
    {
        width: '50',
        key: 'operationType',
        title: 'Operation type',
        render: (props) => {
            const operationType = (() => {
                switch (props.rowData.operationType) {
                    case EOperationType.INCOME: {
                        return 'Income';
                    }
                    case EOperationType.OUTCOME: {
                        return 'Outcome';
                    }
                    default:
                        return '';
                }
            })();
            return <span>{operationType}</span>;
        },
    },
    {
        width: '50',
        key: 'status',
        title: 'Status',
        render: (props) => {
            switch (props.rowData.status) {
                case ETransactionStatus.PENDING: {
                    return <Plank colorType={'secondary'}>Pending</Plank>;
                }
                case ETransactionStatus.COMPLETED: {
                    return <Plank colorType={'primary'}>Completed</Plank>;
                }
                case ETransactionStatus.REJECTED: {
                    return <Plank colorType={'tertiary'}>Rejected</Plank>;
                }
                default:
                    return <></>;
            }
        },
    },
    {
        title: 'Additional Notes',
        width: '100',
        render: (props) => {
            // Transaction Amount Rp 100.000; Fee amount Rp 20.000
            if (props.rowData.type === ETransactionType.QR_PAYMENT) {
                const message = `Transaction Amount Rp ${props.rowData.meta.qrAmount}; Fee amount Rp ${props.rowData.meta.qrFee}`
                return <>{message}</>;
            }
            if (props.rowData.type === ETransactionType.QR_PAYMENT_REFUND) {
                const message = `Refund Amount Rp ${props.rowData.amount}`
                return <>{message}</>;
            }
            if (props.rowData.type === ETransactionType.INTERBANK) {
                const meta = props.rowData.meta;
                let bankName = meta.bankName || meta.beneficiary_bank_name
                const beneficiaryBankCode = meta.beneficiary_bank_code;
                if (!bankName && BankList[beneficiaryBankCode]) {
                    bankName = BankList[beneficiaryBankCode].bank_name
                }
                const additionalNotes = `${bankName} (${meta.beneficiary_bank_code})`;
                return <>{additionalNotes}</>;
            }
            return <>{''}</>;
        },
    },
    {
        title: 'Merchant / Destination Account Name',
        width: '100',
        render: (props) => {
            if ([ETransactionType.QR_PAYMENT, ETransactionType.QR_PAYMENT_REFUND].includes(props.rowData.type)) {
                const merchantName = `${props.rowData.meta.qrMerchantName}, ${props.rowData.meta.qrMerchantCity}`;
                return <>{merchantName}</>;
            }
            if (props.rowData.type === ETransactionType.PAYMENT) {
                const merchantName = props.rowData.meta.orderSubMerchantName;
                return <>{merchantName}</>;
            }
            const meta = props.rowData.meta;
            return <>{meta.accountName || meta.beneficiary_account_name}</>;
        },
    },
    {
        title: 'Merchant / Destination Account Num.',
        width: '100',
        render: (props) => {
            if ([ETransactionType.QR_PAYMENT, ETransactionType.QR_PAYMENT_REFUND].includes(props.rowData.type)) {
                return <>{props.rowData.meta.qrMerchantPan}</>;
            }
            const meta = props.rowData.meta;
            return <>{meta.accountNumber || meta.beneficiary_account_number}</>;
        },
    },
    {
        width: '100',
        key: 'referenceNo',
        title: 'Partner Reference ID',
        render: (props) => {
            return <>{ props.rowData.referenceNo ?? '-' }</>;
        },
    },
    {
        width: '100',
        key: 'uniqueTransactionId',
        title: 'Unique Transaction ID (Bank, VA)',
        render: (props) => {
            return <>{ props.rowData.uniqueTransactionId ?? '-' }</>;
        },
    },
];

export default columns;
